import React, { useMemo } from "react";
import { Checkbox } from "../../../../shared/Checkbox/Checkbox";
import { useAppTranslation } from "../../../../i18n/useAppTranslation";
import { PROCEDURE_STATE_TYPES } from "../../../../consts/general";
import { unwrapOr } from "../../../../utilities/general";
import { CompleteIcon } from "../../components/CompleteIcon/CompleteIcon";
import { ProcedureTableInfo } from "../../components/ProcedureTableInfo/ProcedureTableInfo";
import { CompleteLink } from "../../components/CompleteLink/CompleteLink";
import {
  getProcedureRoutes,
  getProcedureStatuses,
} from "../../ProviderRoom.utils";
import { useRoomStoreMultiselect } from "../../hooks/useRoomStoreMultiselect";
import parentClasses from "../../sass/ProviderRoom.module.scss";

const type = PROCEDURE_STATE_TYPES.pending;

export function useTableHealth({
  procedures = [],
  showHideActionButton,
  providerUserId,
  signList,
}) {
  const { tCommon } = useAppTranslation.Common();

  const {
    isSelected,
    isSelectedAll,
    onSelectAll,
    onSelectOne,
    resetSelected,
    isSelectedPartial,
  } = useRoomStoreMultiselect({
    allEntities: procedures.map((p) => p.id),
    roomSignList: signList,
  });

  const cols = [
    {
      data: (
        <Checkbox
          inverted
          size="small"
          isChecked={isSelectedAll()}
          isIntermediate={isSelectedPartial()}
          onChange={onSelectAll}
        />
      ),
      accessor: "selectCheck",
    },
    {
      data: tCommon("providerRoom.table.procedureInfo"),
      accessor: "info",
    },
    {
      data: tCommon("label.patient"),
      accessor: "patientName",
    },
    {
      data: tCommon("providerRoom.table.clinic"),
      accessor: "clinicName",
    },
    {
      data: tCommon("providerRoom.table.traceability"),
      accessor: "hasTraceability",
      headColClassName: parentClasses.tableHeadCentered,
    },
    {
      data: tCommon("providerRoom.table.notes"),
      accessor: "hasNotes",
      headColClassName: parentClasses.tableHeadCentered,
    },
    {
      data: tCommon("providerRoom.table.consent"),
      accessor: "hasConsent",
      headColClassName: parentClasses.tableHeadCentered,
    },
    {
      data: tCommon("providerRoom.table.questionnaire"),
      accessor: "hasQuestionnaire",
      headColClassName: parentClasses.tableHeadCentered,
    },
    {
      data: tCommon("providerRoom.table.prescription"),
      accessor: "hasPrescription",
      headColClassName: parentClasses.tableHeadCentered,
    },
  ];

  const data = useMemo(
    () =>
      procedures.map((p) => {
        const {
          hasNotes,
          hasConsent,
          hasTraceability,
          hasQuestionnaire,
          hasPrescription,
        } = getProcedureStatuses(p);

        const {
          infoRoute,
          notesRoute,
          consentRoute,
          viewQuestionnaireRoute,
          viewConsentRoute,
          questionnaireRoute,
          prescriptionRoute,
        } = getProcedureRoutes({
          procedure: p,
          type,
        }).health;
        const routeWithState = {
          pathname: infoRoute,
          state: {
            chartUserId: providerUserId,
            isFromChartAuditReport: showHideActionButton,
          },
        };
        return {
          key: p.id,

          selectCheck: (
            <Checkbox
              size="small"
              isChecked={isSelected(p.id)}
              onChange={() => onSelectOne(p.id)}
              isDisabled={showHideActionButton}
            />
          ),

          info: (
            <ProcedureTableInfo
              title={p.name}
              rawDate={p.date}
              link={
                showHideActionButton || providerUserId
                  ? routeWithState
                  : infoRoute
              }
            />
          ),

          patientName: unwrapOr(
            () => p.patient.fullName,
            tCommon("symbol.longDash"),
          ),

          clinicName: unwrapOr(
            () => p.clinic.clinic_name,
            tCommon("symbol.longDash"),
          ),

          hasTraceability: <CompleteIcon isCompleted={hasTraceability} />,

          hasNotes: (
            <CompleteLink
              isCompleted={hasNotes}
              to={notesRoute}
              isDisabled={showHideActionButton}
            >
              <CompleteIcon isCompleted={hasNotes} />
            </CompleteLink>
          ),

          hasConsent: (
            <CompleteLink
              isCompleted={hasConsent}
              to={hasConsent ? consentRoute : viewConsentRoute}
              isDisabled={showHideActionButton}
            >
              <CompleteIcon isCompleted={hasConsent} />
            </CompleteLink>
          ),

          hasQuestionnaire: (
            <CompleteLink
              isCompleted={hasQuestionnaire}
              to={
                hasQuestionnaire ? questionnaireRoute : viewQuestionnaireRoute
              }
              isDisabled={showHideActionButton}
            >
              <CompleteIcon isCompleted={hasQuestionnaire} />
            </CompleteLink>
          ),

          hasPrescription: hasPrescription ? (
            <CompleteLink
              isCompleted={hasPrescription}
              to={prescriptionRoute}
              isDisabled={showHideActionButton}
            >
              <CompleteIcon isCompleted={hasPrescription} />
            </CompleteLink>
          ) : (
            <CompleteIcon isCompleted={hasPrescription} />
          ),
        };
      }),
    [procedures, signList],
  );

  return {
    cols,
    data,
    resetSelected,
  };
}
